import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, Subject, tap } from 'rxjs';

import { AppIcon, AppIconName } from '../icons/icon.type';
import { ExtrasType } from './input-extras/input-extras.component';

@Component({
  selector: 'app-input',
  template: `
    <app-icon class="icon" *ngIf="icon" [icon]="icon"></app-icon>
    <input
      class="inp"
      type="{{ type }}"
      style="transition: background-color 50000s ease-in-out 0s; font-feature-settings: 'kern' 0, 'liga' 0, 'clig' 0, 'calt' 0"
      [attr.placeholder]="placeholder"
      [attr.name]="name"
      [attr.enterkeyhint]="enterkeyhint"
      [attr.inputmode]="inputmode"
      [attr.maxlength]="maxLength"
      [attr.minlength]="minLength"
      [attr.autocomplete]="autocomplete ? autocomplete : masked ? 'off' : 'on'"
      #input />
    @if (maxLength) {
      <div class="counter">{{ (changed$ | async)?.length || 0 }}/{{ maxLength }}</div>
    }
    <ng-content></ng-content>
    <app-input-extras *ngIf="extras" class="extras" [extras]="extras"></app-input-extras>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  styleUrls: ['./styles/input.scss', './styles/input-validation.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @ViewChild('input', { static: true })
  inputRef!: ElementRef<HTMLInputElement>;

  @Input()
  @HostBinding('class.with-icon')
  icon?: AppIconName | AppIcon;

  @Input()
  @HostBinding('attr.type')
  type = 'text';

  @Input()
  value: string | number = '';

  @Input()
  enterkeyhint: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send' = 'enter';

  @Input()
  placeholder: string | number = '';

  @Input()
  autocomplete?: string;

  @Input()
  name: string | null = null;

  @Input()
  inputmode: string | null = null;

  @Input()
  disabled: boolean = false;

  @Input()
  extras?: ExtrasType[];

  @Input()
  @HostBinding('class.with-counter')
  maxLength?: number;

  @Input()
  minLength?: number;

  @Input()
  masked?: boolean;

  @Input()
  @HostBinding('attr.decoration')
  decoration?: string;

  @Output()
  changed$ = new Subject<string>();

  @Output()
  selectionStart = new EventEmitter<number>();

  @Output()
  keyboardEvent = new EventEmitter<KeyboardEvent>();

  get focused(): boolean {
    return this.active;
  }

  protected active = false;
  protected onControlTouched = () => {};
  protected onControlChanged = (_: string | number | object | null) => {};
  protected destroyRef = inject(DestroyRef);

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    protected renderer: Renderer2,
  ) {
    renderer.addClass(elementRef.nativeElement, 'app-control');
  }

  ngOnChanges(changes: SimpleChanges<InputComponent>): void {
    if (changes.value) {
      this.setInputValue(this.value.toString() || '');
    }
  }

  ngOnInit(): void {
    fromEvent(this.inputRef.nativeElement, 'focus')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onFocus());
    fromEvent<InputEvent>(this.inputRef.nativeElement, 'input')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => this.onInput(e));
    fromEvent(this.inputRef.nativeElement, 'blur')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onBlur());

    fromEvent<KeyboardEvent>(this.inputRef.nativeElement, 'keyup')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(e => {
          this.keyboardEvent.emit(e);
          const selectStart = this.inputRef.nativeElement.selectionStart;
          if (selectStart) {
            this.selectionStart.emit(selectStart);
          }
        }),
      )
      .subscribe();

    if (this.disabled) {
      this.renderer.addClass(this.elementRef.nativeElement, 'disabled');
    }
  }

  protected onInput(_e: InputEvent): void {
    const value = this.getInputValue();
    this.onControlChanged(value);
    this.changed$.next(value);
  }

  protected onFocus(): void {
    if (this.masked) {
      this.inputRef.nativeElement.setAttribute('type', 'password');
    }
    this.active = true;
  }

  protected onBlur(): void {
    this.active = false;
    this.onControlTouched();
  }

  protected getInputValue(): string {
    return this.inputRef.nativeElement.value;
  }

  protected setInputValue(v: string): void {
    this.inputRef.nativeElement.value = v;
    this.changed$.next(v);
  }

  focus(): void {
    this.inputRef.nativeElement.focus();
  }

  blur(): void {
    this.inputRef.nativeElement.blur();
  }

  clear(): void {
    this.setInputValue('');
    this.onControlChanged('');
  }

  writeValue(value: object | string | number | null | undefined): void {
    this.setInputValue(value?.toString() || '');
  }

  registerOnChange(fn: (val: string | number | object | null) => void): void {
    this.onControlChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onControlTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', '');
      this.renderer.setAttribute(this.inputRef.nativeElement, 'disabled', '');
    } else {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
      this.renderer.removeAttribute(this.inputRef.nativeElement, 'disabled');
    }
  }
}
